import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  flagOptions,
  getColumnsMap,
  getFilterConditions,
  onColumnFilterTypeChange,
  onColumnIdChange,
} from '@/helpers/filterPanelHelper';
import { prettyPrintCondition, prettyPrintDateCondition } from '@/Utils/filterConditions';
import { getFilterType } from '@/Utils/getFilterType';
import withFilterPanel from './withFilterPanelHOC';
import FreeformFilter from './FreeformFilter';
import SuggestionBasedFilter from './SuggestionBasedFilter';
import InRange from '@/Components/Modals/FilterPanel/FilterPanelInRange';
import { Select } from '@/Components/UI/Form/Select/Select';
import ComboBox from '@/Components/UI/Shadcn/ComboBox';
import { DatePicker } from '@/Components/UI/Shadcn/DatePicker';
import Icon from '@/Components/UI/Icon/Icon';
import { spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import AndOrIndentSVG from './AndOrIndentSVG.tsx';
import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import FilterFilePickerModal from '@/Components/Modals/FilterFilePickerModal/FilterFilePickerModal';
import { Button } from '@/Components/UI/Button/Button';
import { get } from '@/Utils/API';
import {
  GENERAL_ERROR_TEXT,
  TOAST_TEXT_ADD_FILE_TO_FOLDER_ERROR,
  TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
  TOAST_TYPE_ERROR,
  URL_DATASET,
} from '@/Utils/constants';
import showToast from '@/Components/Toast/showToastTemplate';

const andOrOptions = [
  { label: 'and', value: 'and', isChecked: false },
  { label: 'or', value: 'or', isChecked: true },
];

const booleanOptions = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
];

const FilterPanelOrClause = ({
  filterModelElement,
  index,
  onFilterChange,
  onChangeFilterType,
  setIsDisabledApplyButton,
  setAnyFilterData,
  isMultiValueEnabled,
  onRemove,
  blankConditions,
  ifCondition,
  ifGroupIndex,
  isValidConditions,
  isAggFilter,
}) => {
  const { colId, isChecked, type, filter, filterTo, isCaseSensitive, otherHandle, otherColumn } =
    filterModelElement;

  const [selectedColId, setSelectedColId] = useState(colId);
  const [selectedIsChecked, setSelectedIsChecked] = useState(isChecked);
  const [selectedType, setSelectedType] = useState(type);
  const [selectedFilter, setSelectedFilter] = useState(filter);
  const [selectedFilterTo, setSelectedFilterTo] = useState(filterTo);
  const [selectedIsCaseSensitive, setSelectedIsCaseSensitive] = useState(isCaseSensitive);
  const [selectedOtherHandle, setSelectedOtherHandle] = useState(otherHandle);
  const [selectedOtherColumn, setSelectedOtherColumn] = useState(otherColumn);

  // State specific for "Found in Sheet" / Intersect filter type
  const [isFilePickerModalOpen, setFilePickerModalOpen] = useState(false);
  const [foundInFileMetadata, setFoundInFileMetadata] = useState(null);
  const [foundInFileColumn, setFoundInFileColumn] = useState(null);

  const { user } = useSelector((state) => state[userDataSliceName]);
  const {
    columnDefs,
    clientState: { columnState },
  } = useSelector((state) => state[spreadsheetSliceName]);

  const columns = useMemo(() => getColumnsMap(columnDefs), [columnDefs]);
  const rowColumnsOptions = useMemo(
    () =>
      Object.values(columns).map((el) => ({
        ...el,
        value: el.colId,
        label: el.name || el.colId,
        type: el.fieldType,
      })),
    [columns]
  );
  const aggColumnsOptions = useMemo(
    () =>
      columnDefs.reduce((acc, column) => {
        const isAggFuncReturnsNumber = (type, aggFunc) => {
          if (getFilterType(type) !== 'number') {
            if (
              aggFunc === 'min' ||
              aggFunc === 'max' ||
              aggFunc === 'first' ||
              aggFunc === 'last' ||
              aggFunc === 'mode'
            ) {
              return false;
            }
            return true;
          }
          return true;
        };
        const isValidAggFunc = (type, aggFunc) => {
          if (getFilterType(type) === 'date') {
            return true;
          } else {
            return isAggFuncReturnsNumber(type, aggFunc);
          }
        };
        const correspondingColumn = columnState.find(
          ({ colId, aggFunc }) =>
            column.colId === colId && aggFunc && isValidAggFunc(column.fieldType, aggFunc)
        );
        if (correspondingColumn) {
          acc.push({
            value: column.colId,
            label: `(${correspondingColumn.aggFunc}) ${column.headerName}`,
            type: isAggFuncReturnsNumber(column.fieldType, correspondingColumn.aggFunc)
              ? 'Int64'
              : column.fieldType,
          });
        }

        return acc;
      }, []),
    [columnDefs, columnState]
  );
  const columnsOptions = isAggFilter ? aggColumnsOptions : rowColumnsOptions;

  const [isWrongRegexValue, showRegexValidation] = useState(false);
  const [isInputWithSuggestions, setIsInputWithSuggestions] = useState(null);

  const [selectedColumn, setSelectedColumn] = useState(
    columnsOptions.find((column) => column.colId === colId)
  );

  const [conditions, setConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState(null);

  const { maxFilters } = user.userProperties;

  const [activeAndOr, setActiveAndOr] = useState(() => {
    const matchingOption = andOrOptions.find((option) => option.isChecked === isChecked);
    return matchingOption ? matchingOption.value : 'and';
  });

  const currFieldType = isAggFilter ? selectedColumn?.type : columns[selectedColId]?.fieldType;
  const isFirstFilter = index === 0;

  useEffect(() => {
    const foundColumn =
      columnsOptions.find((column) => {
        const _colId = isAggFilter ? column.value : column.colId;
        return _colId === selectedColId;
      }) || null;
    setSelectedColumn(foundColumn);
  }, [selectedColId, columnsOptions]);

  useEffect(() => {
    if (selectedOtherHandle?.length > 0) {
      getFileMetadata(selectedOtherHandle).then((metadata) => {
        if (!metadata) {
          console.error('Failed to fetch metadata');
        }
        setFoundInFileMetadata(metadata);
      });
    }
  }, [selectedOtherHandle]);

  useEffect(() => {
    if (selectedOtherColumn?.length > 0 && foundInFileMetadata) {
      setFoundInFileColumn({
        label: getHeaderForColId(selectedOtherColumn),
        value: selectedOtherColumn,
      });
    }
  }, [selectedOtherColumn, foundInFileMetadata, selectedOtherHandle]);

  useEffect(() => {
    setSelectedCondition(conditions.find((condition) => condition.value === selectedType) || null);
  }, [selectedType, conditions]);

  useEffect(() => {
    if (selectedColumn) {
      const newConditions = getFilterConditions(selectedColId, currFieldType).map((el) => ({
        value: el,
        label:
          currFieldType === 'DateTime' || currFieldType === 'DateTime64'
            ? prettyPrintDateCondition(el)
            : prettyPrintCondition(el),
      }));
      setConditions(newConditions);
    }
  }, [selectedColId, currFieldType, selectedColumn, selectedType]);

  useEffect(() => {
    setSelectedColId(colId);
    setSelectedIsChecked(isChecked);
    setSelectedType(type);
    setSelectedFilter(filter);
    setSelectedFilterTo(filterTo);
    setSelectedIsCaseSensitive(isCaseSensitive);
    setSelectedOtherHandle(otherHandle);
    setSelectedOtherColumn(otherColumn);
  }, [filterModelElement]);

  const onChangeModelKeyValue = (key, modelValue) => {
    let _filterModelElement = { ...filterModelElement };

    if (key === 'colId') {
      const baseFieldType = isAggFilter ? selectedColumn?.type : columns[modelValue].fieldType;
      const filterType = getFilterType(baseFieldType);
      _filterModelElement = { ...filterModelElement, filterType };
      onColumnIdChange(
        key,
        index,
        selectedColId,
        currFieldType,
        filterType,
        columns,
        modelValue,
        _filterModelElement,
        showRegexValidation,
        onFilterChange,
        ifGroupIndex
      );
      setSelectedColId(modelValue);
    } else if (key === 'type') {
      const baseFieldType = isAggFilter ? selectedColumn?.type : columns[selectedColId].fieldType;
      const filterType = getFilterType(baseFieldType);
      _filterModelElement = { ...filterModelElement, filterType };
      onColumnFilterTypeChange(
        key,
        index,
        modelValue,
        _filterModelElement,
        onFilterChange,
        ifGroupIndex
      );
      setSelectedType(modelValue);
    } else if (key === 'isChecked') {
      setActiveAndOr(modelValue ? 'or' : 'and'); // Automatically update the and/or value
      setSelectedIsChecked(modelValue);
      _filterModelElement.isChecked = modelValue;
      onFilterChange(index, _filterModelElement, ifGroupIndex);
    } else {
      onFilterChange(
        index,
        {
          ..._filterModelElement,
          [key]: modelValue,
        },
        ifGroupIndex
      );
      if (key === 'filter') setSelectedFilter(modelValue);
      if (key === 'filterTo') setSelectedFilterTo(modelValue);
      if (key === 'isCaseSensitive') setSelectedIsCaseSensitive(!selectedIsCaseSensitive);
      if (key === 'otherHandle') setSelectedOtherHandle(modelValue);
      if (key === 'otherColumn') setSelectedOtherColumn(modelValue);
    }
  };

  const getFileMetadata = async (fileUuid) => {
    const endpoint = `${URL_DATASET}/${fileUuid}`;
    try {
      const getMetadataResponse = await get(endpoint, true);
      return await getMetadataResponse.json();
    } catch (error) {
      showToast({
        type: TOAST_TYPE_ERROR,
        text: TOAST_TEXT_GENERAL_SPREADSHEET_ERROR,
        errorContext: error,
        endpoint: endpoint,
        fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
      });
      return null;
    }
  };

  const getHeaderForColId = (colId) => {
    let header = '';
    try {
      header =
        foundInFileMetadata.Headers[
          Object.keys(foundInFileMetadata.HeaderToColumnMapping).find(
            (key) => foundInFileMetadata.HeaderToColumnMapping[key] === colId
          )
        ];
    } catch (e) {
      console.error(`Failed to get header for column id ${colId}`, e);
    }
    return header;
  };

  const getFoundInFileColumnOptions = () => {
    if (!foundInFileMetadata?.Headers || !foundInFileMetadata?.HeaderToColumnMapping) {
      return [];
    }

    const { Headers, HeaderToColumnMapping, FieldsTypes } = foundInFileMetadata;
    return Headers.map((header, idx) => ({
      label: header,
      value: HeaderToColumnMapping[idx],
      fieldType: FieldsTypes?.[idx],
    })).filter(({ fieldType }) => fieldType === currFieldType);
  };

  const onCaseSensitiveChangeEvent = () => {
    onChangeModelKeyValue('isCaseSensitive', !selectedIsCaseSensitive);
  };

  const isComponentDisabled = maxFilters <= index;

  useEffect(() => {
    if (selectedType) {
      setIsInputWithSuggestions(selectedType.includes('Any') && selectedColId !== '*');
    }
  }, [selectedType, selectedColId]);

  useEffect(() => {
    if (selectedType === 'regularExpression') {
      try {
        new RegExp(selectedFilter);
        showRegexValidation(false);
        onChangeModelKeyValue('isValidRegex', true);
      } catch (e) {
        showRegexValidation(true);
        onChangeModelKeyValue('isValidRegex', false);
      }
    }
  }, [selectedFilter, selectedType]);

  const filterPanelColumnComboBoxElement = (
    <ComboBox
      value={selectedColumn ? [selectedColumn] : []}
      options={columnsOptions}
      onChange={(newValue) => onChangeModelKeyValue('colId', newValue[0].value)}
      placeholder='Column'
      commandProps={{ className: 'max-w-[175px]' }}
      disabled={isComponentDisabled}
      dataCy='filter-panel-column'
      dataTestId='filter-panel-column'
    />
  );

  return (
    <div
      className={clsx(
        'flex justify-between items-center',
        selectedIsChecked && !isFirstFilter && 'ml-8',
        isComponentDisabled && 'relative'
      )}
    >
      {selectedIsChecked && !isFirstFilter && <AndOrIndentSVG />}
      <span
        className={clsx(isComponentDisabled && '!cursor-not-allowed absolute w-full h-full z-10')}
      />
      <div className={'flex justify-items-start w-full gap-2 mb-2 items-center'}>
        {isFirstFilter ? (
          <p className={'!w-16 !m-0 justify-center h-[34px] flex flex-nowrap items-center'}>
            {ifCondition || 'Where'}
          </p>
        ) : (
          <div className='w-16'>
            <Select
              onChange={(value) => {
                if (value === activeAndOr) return;
                setActiveAndOr(value);
                if (ifCondition) {
                  onChangeFilterType(index, ifGroupIndex);
                }
                onChangeFilterType(index);
              }}
              options={andOrOptions}
              value={activeAndOr}
              disabled={isComponentDisabled}
              buttonClassName='!w-16'
              dropdownClassName='!w-16'
              color='midnight'
              dataCy={`and-or-select-${index}`}
            />
          </div>
        )}

        {selectedColumn?.name && selectedColumn.name.length > 16 ? (
          <Tooltip side='top' text={selectedColumn.name}>
            {filterPanelColumnComboBoxElement}
          </Tooltip>
        ) : (
          filterPanelColumnComboBoxElement
        )}

        <ComboBox
          value={selectedCondition ? [selectedCondition] : []}
          options={conditions}
          onChange={(newValue) => onChangeModelKeyValue('type', newValue[0].value)}
          placeholder='Operator'
          commandProps={{ className: 'max-w-[175px]' }}
          disabled={!selectedColId || isComponentDisabled}
          dataCy='filter-panel-operator'
          dataTestId='filter-panel-operator'
        />

        {renderFilterValueInput()}
        {renderInRangePicker()}
        {renderCaseSensitiveAndRemoveButtons()}
      </div>
      <FilterFilePickerModal
        show={isFilePickerModalOpen}
        hideModal={() => setFilePickerModalOpen(false)}
        setSelectedFile={(file) => {
          onChangeModelKeyValue('otherHandle', file.FileUuid);
          setFilePickerModalOpen(false);
        }}
      />
    </div>
  );

  function renderFilterValueInput() {
    if (selectedType === 'intersect' || selectedType === 'except') {
      return (
        <>
          <p className='ml-1 text-sm text-ui-secondary'>Sheet:</p>
          <Button
            onClick={() => setFilePickerModalOpen(true)}
            data-cy='filter-panel-browse-file'
            color='oceanBlue'
            variant='outline'
            className='max-w-56'
            iconName={foundInFileMetadata?.FileName ? 'file-search' : 'upload-simple'}
          >
            <Tooltip side='bottom' text={foundInFileMetadata?.FileName || 'Browse'} asChild>
              <p className='mb-0 overflow-hidden font-medium whitespace-nowrap text-ellipsis'>
                {foundInFileMetadata?.FileName || 'Browse'}
              </p>
            </Tooltip>
          </Button>
          {foundInFileMetadata && (
            <>
              <p className='ml-1 text-sm text-ui-secondary'>Column(s):</p>
              <ComboBox
                value={foundInFileColumn ? [foundInFileColumn] : []}
                options={getFoundInFileColumnOptions()}
                onChange={(newValue) => {
                  onChangeModelKeyValue('otherColumn', newValue[0].value);
                  setFoundInFileColumn(newValue[0]);
                }}
                placeholder='Select Column'
                commandProps={{ className: 'w-full max-w-[260px]' }}
                disabled={!foundInFileMetadata}
                dataCy='filter-panel-file-column'
              />
            </>
          )}
        </>
      );
    }

    if (currFieldType === 'Boolean' && !blankConditions) {
      const activeBooleanOption = booleanOptions.find((option) => option.value === selectedFilter);
      return (
        <div className='w-[35%]'>
          <ComboBox
            value={selectedColId ? [activeBooleanOption] : []}
            dataCy='filter-panel-input'
            dataTestId='filter-panel-input'
            options={booleanOptions}
            onChange={(newValue) => {
              onChangeModelKeyValue('filter', newValue[0].value);
            }}
            placeholder='Value'
            disabled={!selectedColId || isComponentDisabled}
          />
        </div>
      );
    }

    if (currFieldType === 'metadata_tag' && !blankConditions) {
      const activeFlagFilter = flagOptions.find((option) => option.value === selectedFilter);
      return (
        <ComboBox
          value={[activeFlagFilter] || []}
          dataCy='filter-panel-input'
          dataTestId='filter-panel-input'
          options={flagOptions}
          onChange={(newValue) => {
            onChangeModelKeyValue('filter', newValue[0].value);
          }}
          placeholder='Value'
          disabled={!selectedColId || isComponentDisabled}
          commandProps={{ className: 'max-w-[175px]' }}
        />
      );
    }

    if (
      currFieldType !== 'Boolean' &&
      currFieldType !== 'DateTime' &&
      currFieldType !== 'DateTime64' &&
      currFieldType !== 'metadata_tag' &&
      selectedType &&
      selectedType !== 'inRange' &&
      !blankConditions
    ) {
      return (
        <>
          {isInputWithSuggestions === true && (
            <div className='w-[35%]'>
              <SuggestionBasedFilter
                cypressTextFieldName={'filter-panel-input'}
                defaultValue={selectedColId ? selectedFilter : ''}
                onChange={(newValue) => onChangeModelKeyValue('filter', newValue)}
                disabled={!selectedType || isComponentDisabled}
                currFieldType={currFieldType}
                colId={selectedColId}
                operatorType={selectedType}
              />
            </div>
          )}
          {isInputWithSuggestions === false && (
            <div className='w-[35%]'>
              <FreeformFilter
                type={selectedType}
                cypressTextFieldName={'filter-panel-input'}
                showRegexInvalidBlock={isWrongRegexValue}
                setIsDisabledApplyButton={setIsDisabledApplyButton}
                isMultiple={isMultiValueEnabled}
                disabled={!selectedType || isComponentDisabled}
                defaultValue={selectedColId ? selectedFilter : ''}
                currFieldType={currFieldType}
                onChange={(newValue) => onChangeModelKeyValue('filter', newValue)}
                indexInFilterModel={index}
                setAnyFilterData={setAnyFilterData}
              />
            </div>
          )}
        </>
      );
    }
    return null;
  }

  function renderInRangePicker() {
    if (
      (currFieldType === 'DateTime' || currFieldType === 'DateTime64') &&
      !blankConditions &&
      selectedType &&
      selectedType !== 'intersect' &&
      selectedType !== 'except'
    ) {
      return (
        <>
          {selectedType === 'inRange' ? (
            <>
              <DatePicker
                className='max-max-w-[175px]'
                onChange={(startDate) => {
                  onChangeModelKeyValue(
                    'filter',
                    (new Date(startDate).getTime() / 1000).toString()
                  );
                }}
                value={selectedFilter}
                granularity='minute'
                disabled={isComponentDisabled}
                placeholder='Start Date'
              />
              <DatePicker
                className='max-max-w-[175px]'
                onChange={(endDate) => {
                  onChangeModelKeyValue(
                    'filterTo',
                    (new Date(endDate).getTime() / 1000).toString()
                  );
                }}
                value={selectedFilterTo}
                granularity='minute'
                disabled={isComponentDisabled}
                placeholder='End Date'
              />
            </>
          ) : (
            <DatePicker
              className='max-max-w-[175px]'
              onChange={(date) => {
                onChangeModelKeyValue('filter', (new Date(date).getTime() / 1000).toString());
              }}
              value={selectedFilter}
              granularity='minute'
              disabled={isComponentDisabled}
            />
          )}
        </>
      );
    }

    if (
      selectedType === 'inRange' &&
      currFieldType !== 'DateTime' &&
      currFieldType !== 'DateTime64' &&
      !blankConditions
    ) {
      return (
        <InRange
          onChange={onChangeModelKeyValue}
          defaultStartValue={selectedFilter}
          defaultEndValue={selectedFilterTo}
          id={index}
          modelType={selectedType}
          indexInFilterModel={index}
          ifCondition={ifCondition}
          isValidConditions={isValidConditions}
          type={selectedType}
          cypressTextFieldName={'filter-panel-input'}
          isWrongRegexValue={isWrongRegexValue}
          setIsDisabledApplyButton={setIsDisabledApplyButton}
          isMultiValueEnabled={isMultiValueEnabled}
          disabled={!selectedType || isComponentDisabled}
          defaultValue={selectedColId ? selectedFilter : ''}
          currFieldType={currFieldType}
          setAnyFilterData={setAnyFilterData}
          colId={selectedColId}
          filter={selectedFilter}
          filterTo={selectedFilterTo}
        />
      );
    }
    return null;
  }

  function renderCaseSensitiveAndRemoveButtons() {
    return (
      <div className='inline-flex'>
        {!blankConditions && isInputWithSuggestions && (
          <Tooltip text='Match Case' side='top' asChild>
            <button
              onClick={onCaseSensitiveChangeEvent}
              className={clsx(
                'h-[34px] border px-2 text-ui-secondary hover:bg-capri-100 border-gray-300 text-sm font-medium w-[36px] rounded-l !rounded-r-none',
                selectedIsCaseSensitive && 'bg-capri-100 text-capri-900'
              )}
            >
              <Icon name='text-aa' size={18} />
            </button>
          </Tooltip>
        )}

        <button
          onClick={() => {
            onRemove();
            setSelectedColumn(null);
            setSelectedCondition(null);
            setSelectedFilter('');
            setSelectedFilterTo('');
            setSelectedType('');
            setSelectedIsCaseSensitive(false);
          }}
          className={clsx(
            'h-[34px] w-[36px] px-2 text-sm font-medium border border-gray-300 hover:bg-shadow-100 text-ui-secondary',
            !blankConditions && isInputWithSuggestions ? 'rounded-r border-l-0' : 'rounded'
          )}
          data-cy='remove-btn'
        >
          <Icon name='trash' size={18} />
        </button>
      </div>
    );
  }
};

export default withFilterPanel(FilterPanelOrClause);
